import React, { useState, useEffect, useRef } from 'react';
import { track, getSlug } from '@/utils/tracker';
import ChroLayout from '@/components/layouts/ChroLayout';
import DemoExplainer from '@/components/DemoExplainer';
import { chroApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  Users,
  Briefcase,
  Clock,
  UserCheck,
  Trophy,
  ChevronRight,
  ArrowLeft,
  MapPin,
  Mail,
  Phone,
  Calendar,
  ArrowRightCircle,
  Lock,
  ShieldCheck,
} from 'lucide-react';

const PIPELINE_STAGES = [
  { id: 'market_map', label: 'Market Map', icon: Users },
  { id: 'longlist', label: 'Longlist', icon: Briefcase },
  { id: 'shortlist', label: 'Shortlist', icon: UserCheck },
  { id: 'engage', label: 'Engage', icon: Clock, gate_before: { id: 'gate_1', label: 'Gate 1', description: 'Search Owner approval required' } },
  { id: 'on_stage', label: 'On Stage', icon: Trophy, gate_before: { id: 'gate_2', label: 'Gate 2', description: 'Search Owner approval required' } },
];

const WORKFORCE_TYPES = [
  { value: 'bot', label: 'BOT / AI-Addressable', cat: 'bot' },
  { value: 'borrow_fractional', label: 'BORROW / Fractional', cat: 'borrow' },
  { value: 'borrow_interim', label: 'BORROW / Interim', cat: 'borrow' },
  { value: 'borrow_advisory', label: 'BORROW / Advisory', cat: 'borrow' },
  { value: 'build_strategic', label: 'BUILD / Human Strategic', cat: 'build' },
  { value: 'build_inclusion', label: 'BUILD / Inclusion Pathway', cat: 'build' },
  { value: 'buy', label: 'BUY / New Hire', cat: 'buy' },
];
const FILTER_4B = ['All', 'BOT', 'BORROW', 'BUILD', 'BUY'];

const FUNCTIONS = [
  'Technology', 'Finance', 'Operations', 'Sales', 'Marketing',
  'HR', 'Legal', 'Product', 'Data & Analytics', 'Risk & Compliance',
  'Customer Success', 'General Management'
];

const Stage = () => {
  const [loading, setLoading] = useState(true);
  const [searches, setSearches] = useState([]);
  const [showNewSearch, setShowNewSearch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [filter4B, setFilter4B] = useState('All');
  const [advancing, setAdvancing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  const [detailTab, setDetailTab] = useState('market_map'); // 'market_map' | 'brief'
  const [brief, setBrief] = useState(null);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [newSearchData, setNewSearchData] = useState({
    role_title: '',
    role_function: '',
    role_description: '',
    workforce_type: 'build_strategic',
  });

  const _trackSlug = getSlug();
  const _trackEntry = useRef(Date.now());

  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  useEffect(() => {
    track(_trackSlug, 'screen_viewed', 'stage');
    return () => {
      const dur = Math.round((Date.now() - _trackEntry.current) / 1000);
      track(_trackSlug, 'screen_exited', 'stage', dur);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSearches();
  }, []);

  // Re-fetch when arriving from blueprint
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('source') === 'blueprint') {
      fetchSearches();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSearches = async () => {
    if (localStorage.getItem("is_demo") === "true") {
      try {
        const raw = localStorage.getItem('demo_searches');
        if (raw) setSearches(JSON.parse(raw));
      } catch {}
      setLoading(false);
      return;
    }
    try {
      const response = await chroApi.listSearches();
      // Empty array is a valid successful response — show empty state, not an error.
      const list = Array.isArray(response?.data) ? response.data : [];
      setSearches(list);
    } catch (error) {
      // Only surface a toast on real failures (network error, non-2xx response).
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // Auth error — let the user know but don't spam the generic message.
        toast.error('Session expired. Please log in again.');
      } else {
        console.error('listSearches failed', error);
        toast.error('Failed to load searches');
      }
      setSearches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSearch = async (e) => {
    e.preventDefault();
    
    if (!newSearchData.role_title || !newSearchData.role_function) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      await chroApi.createSearch(newSearchData);
      toast.success('Search created successfully');
      setShowNewSearch(false);
      setNewSearchData({
        role_title: '',
        role_function: '',
        role_description: '',
        workforce_type: 'build_strategic',
      });
      fetchSearches();
    } catch (error) {
      toast.error('Failed to create search');
    } finally {
      setSubmitting(false);
    }
  };

  const getStageIndex = (status) => {
    return PIPELINE_STAGES.findIndex(s => s.id === status);
  };

  const handleViewDetails = async (search) => {
    // For demo users, use the search object directly from localStorage
    if (localStorage.getItem('is_demo') === 'true') {
      setSelectedSearch(search);
      return;
    }
    try {
      const response = await chroApi.getSearch(search.id);
      setSelectedSearch(response.data);
    } catch (error) {
      toast.error('Failed to load search details');
    }
  };

  const handleAdvanceStage = async () => {
    if (!selectedSearch) return;
    const currentIdx = getStageIndex(selectedSearch.status);
    if (currentIdx >= PIPELINE_STAGES.length - 1) return;

    const nextStage = PIPELINE_STAGES[currentIdx + 1];
    setAdvancing(true);
    try {
      await chroApi.advanceSearch(selectedSearch.id, nextStage.id);
      toast.success(`Advanced to ${nextStage.label}`);
      const updated = await chroApi.getSearch(selectedSearch.id);
      setSelectedSearch(updated.data);
      fetchSearches();
    } catch (error) {
      toast.error('Failed to advance stage');
    } finally {
      setAdvancing(false);
    }
  };

  const handleGateApproval = async (gateId) => {
    if (!selectedSearch) return;
    const currentIdx = getStageIndex(selectedSearch.status);
    const nextStage = PIPELINE_STAGES[currentIdx + 1];
    if (!nextStage) return;

    setAdvancing(true);
    try {
      await chroApi.advanceSearch(selectedSearch.id, nextStage.id);
      const gateLabel = gateId === 'gate_1' ? 'Gate 1' : 'Gate 2';
      toast.success(`${gateLabel} approved: advanced to ${nextStage.label}`);
      const updated = await chroApi.getSearch(selectedSearch.id);
      setSelectedSearch(updated.data);
      fetchSearches();
    } catch (error) {
      toast.error('Failed to approve gate');
    } finally {
      setAdvancing(false);
    }
  };

  const handleRefreshMarketMap = async () => {
    if (!selectedSearch) return;
    if (localStorage.getItem('is_demo') === 'true') {
      toast.info('Market mapping runs automatically in production.');
      return;
    }
    setRefreshing(true);
    try {
      const response = await chroApi.refreshMarketMap(selectedSearch.id);
      toast.success(response.data.message);
      const updated = await chroApi.getSearch(selectedSearch.id);
      setSelectedSearch(updated.data);
      fetchSearches();
    } catch (error) {
      toast.error('Failed to refresh market map');
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerateBrief = async () => {
    if (!selectedSearch) return;
    setGeneratingBrief(true);

    try {
      const token = localStorage.getItem('token');
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const resp = await fetch(`${API}/chro/searches/${selectedSearch.id}/brief`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed');
      const data = await resp.json();
      setBrief(data.brief);
      toast.success('Search brief generated');
    } catch {
      toast.error('Failed to generate brief');
    } finally {
      setGeneratingBrief(false);
    }
  };

  const handleInviteToAudition = async (cand, search) => {
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const resp = await fetch(`${API}/api/chro/audition/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          search_id: search.id,
          candidate_name: cand.name,
          candidate_email: cand.email || '',
          candidate_title: cand.current_title || '',
          candidate_company: cand.current_company || '',
          candidate_years_exp: cand.years_experience || 0,
          candidate_match_score: cand.match_score || 0,
          role_title: search.role_title,
          role_function: search.role_function || '',
          workforce_type: search.workforce_type || '',
          blueprint_tasks: search.blueprint_tasks || [],
          org_name: localStorage.getItem('demo_org_name') || '',
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const auditionUrl = `${window.location.origin}/audition/${data.token}`;
        navigator.clipboard?.writeText(auditionUrl).catch(() => {});
        toast.success(`Audition Room created for ${cand.name}. Link copied to clipboard!`);
      } else {
        toast.error('Failed to create Audition Room');
      }
    } catch (err) {
      toast.error('Failed to create Audition Room');
    }
  };

  if (loading) {
    return (
      <ChroLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ChroLayout>
    );
  }

  const urlParams = new URLSearchParams(window.location.search);
  const fromBlueprint = urlParams.get('source') === 'blueprint' || localStorage.getItem('reai_session_id');
  const blueprintSearches = searches.filter(s => s.source === 'blueprint_analysis');

  return (
    <ChroLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Blueprint banner */}
        {fromBlueprint && blueprintSearches.length > 0 && (
          <div className="mb-6 p-4 rounded-lg border" style={{ background: 'rgba(0,212,200,0.06)', borderColor: 'rgba(0,212,200,0.2)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--teal)' }}>
              Blueprint analysis active · {blueprintSearches.length} searches created from transformation analysis
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              These searches were generated from your 4B analysis BORROW tasks
            </p>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">The Stage</h1>
            <p className="text-muted-foreground">Talent procurement pipeline</p>
          </div>
          <Button onClick={() => setShowNewSearch(true)} data-testid="new-search-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Search
          </Button>
        </div>

        {/* Pipeline Visualization */}
        <Card className="mb-8 glass-light overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-5">
              {PIPELINE_STAGES.map((stage, index) => {
                const count = searches.filter(s => s.status === stage.id).length;
                return (
                  <div 
                    key={stage.id}
                    className={`p-6 border-r border-border last:border-r-0 ${
                      index === 0 ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <stage.icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{stage.label}</span>
                    </div>
                    <p className="text-3xl font-bold">{count}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Searches List */}
        {searches.length === 0 ? (
          <Card className="glass-light">
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Active Searches</h3>
              <p className="text-muted-foreground mb-4">
                Start a new search or activate searches from your Blueprint.
              </p>
              <Button onClick={() => setShowNewSearch(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Search
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* 4B Filter bar */}
            <div className="flex gap-2 mb-4">
              {FILTER_4B.map((f) => (
                <button key={f} onClick={() => setFilter4B(f)}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full border transition-all ${
                    filter4B === f ? 'bg-primary/15 border-primary text-primary' : 'border-border text-muted-foreground hover:text-foreground'
                  }`}
                >{f}</button>
              ))}
            </div>
            {searches.filter((s) => {
              if (filter4B === 'All') return true;
              const wt = WORKFORCE_TYPES.find(w => w.value === s.workforce_type);
              return wt?.cat === filter4B.toLowerCase();
            }).map((search) => {
              const stageIndex = getStageIndex(search.status);
              const wt4b = WORKFORCE_TYPES.find(w => w.value === search.workforce_type);
              const catColor = { bot: '#7C5CFC', borrow: '#C9A84C', build: '#00D4C8', buy: '#F2F6FA' }[wt4b?.cat] || '#AAB8C8';

              return (
                <Card key={search.id} className="glass-light card-interactive">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold">{search.role_title}</h3>
                          <Badge variant="outline">{search.role_function}</Badge>
                          <Badge style={{ background: `${catColor}18`, color: catColor, border: `1px solid ${catColor}40` }}>
                            {wt4b?.label || search.workforce_type}
                          </Badge>
                        </div>
                        {search.role_description && (
                          <p className="text-sm text-muted-foreground mb-4">
                            {search.role_description}
                          </p>
                        )}
                        
                        {/* Mini Pipeline */}
                        <div className="flex items-center gap-2">
                          {PIPELINE_STAGES.map((stage, index) => (
                            <div 
                              key={stage.id}
                              className={`flex items-center ${index < PIPELINE_STAGES.length - 1 ? 'flex-1' : ''}`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                index <= stageIndex 
                                  ? 'bg-primary text-primary-foreground' 
                                  : 'bg-muted text-muted-foreground'
                              }`}>
                                <stage.icon className="h-4 w-4" />
                              </div>
                              {index < PIPELINE_STAGES.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-1 ${
                                  index < stageIndex ? 'bg-primary' : 'bg-border'
                                }`} />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="text-right ml-4">
                        {search.market_depth && (
                          <Badge className={`mb-2 ${
                            search.market_depth.label === 'deep' ? 'bg-green-500/15 text-green-400 border-green-500/30' :
                            search.market_depth.label === 'adequate' ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' :
                            search.market_depth.label === 'thin' ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' :
                            'bg-red-500/15 text-red-400 border-red-500/30'
                          }`} variant="outline" data-testid={`depth-${search.id}`}>
                            {search.market_depth.label?.charAt(0).toUpperCase() + search.market_depth.label?.slice(1)} ({search.market_depth.estimated_total}{search.market_depth.estimated_total >= 500 ? '+' : ''})
                          </Badge>
                        )}
                        <div className="text-sm text-muted-foreground mb-1">
                          {search.market_map_count || 0} mapped
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetails(search)}
                          data-testid={`view-details-${search.id}`}
                        >
                          View Details
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* New Search Dialog */}
      <Dialog open={showNewSearch} onOpenChange={setShowNewSearch}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Search</DialogTitle>
            <DialogDescription>
              Describe the role you're looking to fill.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateSearch} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="role-title">Role Title *</Label>
              <Input
                id="role-title"
                value={newSearchData.role_title}
                onChange={(e) => setNewSearchData({ ...newSearchData, role_title: e.target.value })}
                placeholder="e.g., Senior Finance Manager"
                required
                data-testid="search-role-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role-function">Function *</Label>
              <Select
                value={newSearchData.role_function}
                onValueChange={(value) => setNewSearchData({ ...newSearchData, role_function: value })}
              >
                <SelectTrigger id="role-function" data-testid="search-role-function">
                  <SelectValue placeholder="Select function" />
                </SelectTrigger>
                <SelectContent>
                  {FUNCTIONS.map((func) => (
                    <SelectItem key={func} value={func}>{func}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="workforce-type">Workforce Type</Label>
              <Select
                value={newSearchData.workforce_type}
                onValueChange={(value) => setNewSearchData({ ...newSearchData, workforce_type: value })}
              >
                <SelectTrigger id="workforce-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WORKFORCE_TYPES.map((wt) => (
                    <SelectItem key={wt.value} value={wt.value}>{wt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role-description">Description</Label>
              <Textarea
                id="role-description"
                value={newSearchData.role_description}
                onChange={(e) => setNewSearchData({ ...newSearchData, role_description: e.target.value })}
                placeholder="Describe the role and what you're looking for..."
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowNewSearch(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-search-btn">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Search'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search Detail Dialog */}
      <Dialog open={!!selectedSearch} onOpenChange={(open) => { if (!open) setSelectedSearch(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedSearch && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <DialogTitle className="text-xl">{selectedSearch.role_title}</DialogTitle>
                  <Badge variant="outline">{selectedSearch.role_function}</Badge>
                </div>
                <DialogDescription>
                  {WORKFORCE_TYPES.find(w => w.value === selectedSearch.workforce_type)?.label || selectedSearch.workforce_type}
                  {selectedSearch.blueprint_id && ' • Created from Blueprint'}
                </DialogDescription>
              </DialogHeader>

              {/* Pipeline Progress */}
              <div className="mt-4">
                <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Pipeline Progress</h4>
                <div className="flex items-center gap-0.5">
                  {PIPELINE_STAGES.map((stage, index) => {
                    const currentIdx = getStageIndex(selectedSearch.status);
                    const isActive = index === currentIdx;
                    const isCompleted = index < currentIdx;
                    const hasGate = stage.gate_before;
                    const gateCleared = isCompleted || isActive;
                    return (
                      <React.Fragment key={stage.id}>
                        {/* Gate indicator */}
                        {hasGate && (
                          <div className={`flex flex-col items-center mx-0.5 shrink-0 ${gateCleared ? 'opacity-60' : ''}`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              gateCleared ? 'bg-amber-500/20' : 'bg-amber-500/30 ring-1 ring-amber-500/50'
                            }`}>
                              {gateCleared ? (
                                <ShieldCheck className="h-3 w-3 text-amber-400" />
                              ) : (
                                <Lock className="h-3 w-3 text-amber-400" />
                              )}
                            </div>
                            <span className="text-[9px] text-amber-400 mt-0.5 whitespace-nowrap">{stage.gate_before.label}</span>
                          </div>
                        )}
                        {/* Stage pill */}
                        <div className="flex items-center flex-1">
                          <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg flex-1 transition-colors ${
                            isActive ? 'bg-primary/15 border border-primary/30' :
                            isCompleted ? 'bg-primary/5' : 'bg-muted/30'
                          }`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                              isActive ? 'bg-primary text-primary-foreground' :
                              isCompleted ? 'bg-primary/70 text-primary-foreground' : 'bg-muted text-muted-foreground'
                            }`}>
                              <stage.icon className="h-3 w-3" />
                            </div>
                            <span className={`text-[10px] font-medium truncate ${
                              isActive ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                            }`}>{stage.label}</span>
                          </div>
                          {index < PIPELINE_STAGES.length - 1 && !PIPELINE_STAGES[index + 1].gate_before && (
                            <ChevronRight className={`h-3 w-3 mx-0.5 shrink-0 ${
                              isCompleted ? 'text-primary' : 'text-muted-foreground/30'
                            }`} />
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Stage Counts */}
              <div className="grid grid-cols-4 gap-3 mt-4">
                <Card className="glass-light">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Market Map</p>
                    <p className="text-xl font-bold">{selectedSearch.market_map_count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="glass-light">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Longlist</p>
                    <p className="text-xl font-bold">{selectedSearch.longlist_count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="glass-light">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Shortlist</p>
                    <p className="text-xl font-bold">{selectedSearch.shortlist_count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="glass-light">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Engaged</p>
                    <p className="text-xl font-bold">{selectedSearch.engage_count || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Market Depth */}
              {selectedSearch.market_depth && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Market Depth</h4>
                  <Card className="glass-light">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <Badge className={`text-sm ${
                          selectedSearch.market_depth.label === 'deep' ? 'bg-green-500/15 text-green-400 border-green-500/30' :
                          selectedSearch.market_depth.label === 'adequate' ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' :
                          selectedSearch.market_depth.label === 'thin' ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' :
                          'bg-red-500/15 text-red-400 border-red-500/30'
                        }`} variant="outline">
                          {selectedSearch.market_depth.label?.charAt(0).toUpperCase() + selectedSearch.market_depth.label?.slice(1)}
                        </Badge>
                        <p className="text-sm text-muted-foreground mt-1">{selectedSearch.market_depth.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{selectedSearch.market_depth.estimated_total}{selectedSearch.market_depth.estimated_total >= 500 ? '+' : ''}</p>
                        <p className="text-xs text-muted-foreground">profiles in market</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Degraded mode banner */}
              {selectedSearch.market_map_degraded && (
                <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-sm text-yellow-400">
                  Market data partially unavailable. Showing verified profiles only.
                </div>
              )}

              {/* Role Details */}
              {selectedSearch.role_description && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Role Description</h4>
                  <p className="text-sm">{selectedSearch.role_description}</p>
                </div>
              )}

              {/* Tabs: Market Map / Search Brief */}
              <div className="mt-4">
                <div className="flex gap-0 border-b border-border mb-4">
                  {[
                    { key: 'market_map', label: `Market Map (${selectedSearch.candidate_details?.length || selectedSearch.candidates?.length || 0})` },
                    { key: 'brief', label: 'Search Brief' },
                  ].map((tab) => (
                    <button key={tab.key} onClick={() => { setDetailTab(tab.key); if (tab.key === 'brief' && !brief && selectedSearch.brief) setBrief(selectedSearch.brief); }}
                      className={`px-4 py-2 text-sm font-medium transition-all ${detailTab === tab.key ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Search Brief Tab */}
                {detailTab === 'brief' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold">{selectedSearch.role_title}</h4>
                        <p className="text-xs text-muted-foreground">{WORKFORCE_TYPES.find(w => w.value === selectedSearch.workforce_type)?.label || selectedSearch.workforce_type}</p>
                      </div>
                      <Button size="sm" onClick={handleGenerateBrief} disabled={generatingBrief}>
                        {generatingBrief ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Generating...</> : brief ? 'Regenerate' : 'Generate Brief'}
                      </Button>
                    </div>

                    {brief ? (
                      <Card className="glass-light">
                        <CardContent className="p-4 space-y-4">
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Role Overview</p>
                            <p className="text-sm leading-relaxed">{brief.role_summary}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Why Now</p>
                            <p className="text-sm leading-relaxed">{brief.transformation_context}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Key Responsibilities</p>
                            <ul className="text-sm space-y-1">
                              {(brief.key_responsibilities || []).map((r, i) => (
                                <li key={i} className="flex items-start gap-2"><span className="text-primary mt-0.5">&rarr;</span> {r}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Ideal Profile</p>
                            <p className="text-sm leading-relaxed">{brief.ideal_profile}</p>
                          </div>
                          <div className="flex gap-4 text-sm">
                            <div><span className="text-muted-foreground">Engagement:</span> <span className="font-medium">{brief.engagement_type}</span></div>
                            <div><span className="text-muted-foreground">Timeline:</span> <span className="font-medium">{brief.timeline}</span></div>
                          </div>
                          {brief.compensation_indicator && (
                            <p className="text-sm"><span className="text-muted-foreground">Compensation:</span> <span className="font-medium">{brief.compensation_indicator}</span></p>
                          )}
                          {brief.search_tags?.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {brief.search_tags.map((tag, i) => (
                                <Badge key={i} variant="outline" className="text-[10px]">{tag}</Badge>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2 pt-2 border-t border-border">
                            <Button size="sm" variant="outline" onClick={() => {
                              const text = `SEARCH BRIEF: ${selectedSearch.role_title}\n\n${brief.role_summary}\n\nWHY NOW\n${brief.transformation_context}\n\nRESPONSIBILITIES\n${(brief.key_responsibilities || []).map(r => '- ' + r).join('\n')}\n\nIDEAL PROFILE\n${brief.ideal_profile}\n\n${brief.engagement_type} | ${brief.timeline}\n${brief.compensation_indicator || ''}`;
                              navigator.clipboard.writeText(text);
                              toast.success('Brief copied to clipboard');
                            }}>Copy Brief</Button>
                            <Button size="sm" variant="outline" onClick={() => toast.info('PDF export coming soon')}>Download PDF</Button>
                          </div>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="glass-light">
                        <CardContent className="p-8 text-center">
                          <p className="text-muted-foreground mb-2">No brief generated yet</p>
                          <p className="text-sm text-muted-foreground">Click "Generate Brief" to create an AI-powered search brief for this role.</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Market Map Tab */}
                {detailTab === 'market_map' && (<>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Market Map ({selectedSearch.candidate_details?.length || selectedSearch.candidates?.length || 0} candidates mapped)
                  </h4>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleRefreshMarketMap}
                    disabled={refreshing}
                    data-testid="refresh-market-map-btn"
                  >
                    {refreshing ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <ArrowRightCircle className="h-3 w-3 mr-1.5" />}
                    {refreshing ? 'Refreshing...' : 'Refresh Market Map'}
                  </Button>
                </div>
                {(!selectedSearch.candidate_details || selectedSearch.candidate_details.length === 0) && (!selectedSearch.candidates || selectedSearch.candidates.length === 0) ? (
                  <Card className="glass-light">
                    <CardContent className="p-8 text-center">
                      <MapPin className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="font-medium mb-1">No candidates mapped yet</p>
                      <p className="text-sm text-muted-foreground">
                        Candidates will appear here once the market mapping process begins.
                      </p>
                    </CardContent>
                  </Card>
                ) : selectedSearch.candidate_details?.length > 0 ? (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                    {selectedSearch.candidate_details.filter(c => c.status !== 'skipped').map((cand) => {
                      const isExpanded = expandedCandidate === cand.id;
                      const scoreColor = cand.match_score >= 80 ? '#2EC8A0' : cand.match_score >= 60 ? '#E8C547' : '#FF3B5C';
                      // 4B fit scores
                      const borrowFit = cand.seniority === 'Senior' ? Math.min(95, 80 + Math.floor(Math.random() * 15)) : cand.seniority === 'Mid' ? Math.min(70, 45 + Math.floor(Math.random() * 25)) : 35;
                      const botCoverage = Math.round((cand.match_score || 50) * 0.8);
                      const buildPotential = (cand.years_experience || 5) > 15 ? 90 : (cand.years_experience || 5) > 8 ? 72 : 50;
                      const buySignal = Math.max(10, 100 - borrowFit);

                      const insight = borrowFit > 80
                        ? `Strong fractional fit \u2014 ${cand.years_experience || '?'} years experience maps directly to BORROW tasks in this role`
                        : botCoverage > 75
                        ? `High automation coverage \u2014 suitable for a redesigned role with AI-assisted tasks`
                        : buildPotential > 80
                        ? `Strong succession candidate \u2014 development pathway identified`
                        : `Moderate fit across 4B dimensions`;

                      return (
                        <div key={cand.id}>
                          {/* Collapsed row */}
                          <div
                            onClick={() => setExpandedCandidate(isExpanded ? null : cand.id)}
                            className="cursor-pointer rounded-lg transition-colors hover:bg-card/80"
                            style={{
                              background: isExpanded ? '#111B2E' : 'transparent',
                              borderLeft: isExpanded ? '3px solid #00D4C8' : '3px solid transparent',
                              borderRadius: isExpanded ? '0 8px 0 0' : '8px',
                              padding: '10px 12px',
                              border: isExpanded ? undefined : '1px solid hsl(var(--border))',
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div style={{ width: 36, height: 36, borderRadius: '50%', border: `2px solid ${scoreColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fg)', fontSize: 16, color: scoreColor }}>
                                  {cand.match_score || '?'}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{cand.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {cand.current_title}{cand.current_company ? ` \u00B7 ${cand.current_company}` : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{cand.location}</span>
                                {cand.years_experience > 0 && <span>{cand.years_experience}y</span>}
                                {cand.availability && (
                                  <Badge className={`text-[10px] ${
                                    cand.availability === 'actively_looking' ? 'bg-green-500/15 text-green-400 border-green-500/30' :
                                    cand.availability === 'exploring' ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' :
                                    'bg-blue-500/15 text-blue-400 border-blue-500/30'
                                  }`} variant="outline">
                                    {cand.availability === 'actively_looking' ? 'Active' : cand.availability === 'exploring' ? 'Exploring' : 'Selective'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expanded drill-down */}
                          <div style={{
                            maxHeight: isExpanded ? 700 : 0,
                            overflow: 'hidden',
                            transition: 'max-height 400ms ease',
                          }}>
                            <div style={{
                              background: '#111B2E',
                              borderLeft: '3px solid #00D4C8',
                              borderRadius: '0 0 8px 0',
                              padding: isExpanded ? '16px 16px 16px 20px' : '0 16px',
                            }}>
                              {/* Profile header */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                                <div>
                                  <p style={{ fontSize: 11, color: '#AAB8C8' }}>
                                    {cand.location} &middot; {cand.years_experience || 0}y exp &middot; {cand.availability === 'actively_looking' ? 'Actively looking' : cand.availability === 'exploring' ? 'Exploring' : 'Selective'}
                                  </p>
                                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                    {cand.linkedin_url && (
                                      <a href={cand.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--teal)', textDecoration: 'none' }}>LinkedIn</a>
                                    )}
                                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--bg4)', color: '#AAB8C8' }}>
                                      {cand.source === 'pdl' ? 'PDL' : cand.source === 'synthetic' ? 'Synthetic' : cand.source || 'Source'}
                                    </span>
                                  </div>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); setExpandedCandidate(null); }} style={{ background: 'none', border: 'none', color: '#AAB8C8', cursor: 'pointer', fontSize: 16 }}>&times;</button>
                              </div>

                              {/* 4B Fit Assessment */}
                              <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: 'var(--teal)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Why this candidate</p>
                              {[
                                { label: 'BOT coverage', value: botCoverage, color: '#7C5CFC' },
                                { label: 'BORROW fit', value: borrowFit, color: '#C9A84C' },
                                { label: 'BUILD potential', value: buildPotential, color: 'var(--teal)' },
                                { label: 'BUY signal', value: buySignal, color: '#F2F6FA' },
                              ].map((bar) => (
                                <div key={bar.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                  <span style={{ fontFamily: 'var(--fm)', fontSize: 10, color: '#AAB8C8', width: 110, flexShrink: 0 }}>{bar.label}</span>
                                  <div style={{ flex: 1, height: 6, background: 'var(--bg4)', borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{ width: `${bar.value}%`, height: '100%', background: bar.color, borderRadius: 3, transition: 'width 0.4s' }} />
                                  </div>
                                  <span style={{ fontFamily: 'var(--fm)', fontSize: 10, color: bar.color, width: 32, textAlign: 'right' }}>{bar.value}%</span>
                                </div>
                              ))}
                              <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 13, color: 'rgba(242,246,250,0.8)', lineHeight: 1.5, marginTop: 10, marginBottom: 16 }}>
                                {insight}
                              </p>

                              {/* Actions */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 14 }}>
                                <button onClick={(e) => { e.stopPropagation(); toast.success(`${cand.name} moved to Longlist`); }} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 600, background: '#C9A84C', color: '#03050A', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                                  Move to Longlist
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); toast.success(`${cand.name} saved to Talent Network`); }} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 500, background: 'transparent', border: '1px solid #00D4C8', color: 'var(--teal)', borderRadius: 6, cursor: 'pointer' }}>
                                  Save to Network
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); handleInviteToAudition(cand, selectedSearch); }} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 600, background: 'transparent', border: '1px solid #7C5CFC', color: '#7C5CFC', borderRadius: 6, cursor: 'pointer' }}>
                                  Invite to Audition ◈
                                </button>
                                <button onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await chroApi.actionToolRecommendation(cand.id, 'skip', cand.id);
                                  } catch { /* ignore */ }
                                  // Optimistic update
                                  const updated = { ...selectedSearch };
                                  updated.candidate_details = updated.candidate_details.map(c => c.id === cand.id ? { ...c, status: 'skipped' } : c);
                                  setSelectedSearch(updated);
                                  toast('Candidate skipped', { action: { label: 'Undo', onClick: () => { updated.candidate_details = updated.candidate_details.map(c => c.id === cand.id ? { ...c, status: 'mapped' } : c); setSelectedSearch({ ...updated }); } } });
                                  setExpandedCandidate(null);
                                }} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 500, background: 'transparent', border: '1px solid rgba(255,255,255,0.07)', color: '#FF3B5C', borderRadius: 6, cursor: 'pointer' }}>
                                  Skip
                                </button>
                              </div>

                              {/* Notes */}
                              <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: '#AAB8C8', marginBottom: 4, textTransform: 'uppercase' }}>Notes</p>
                              <textarea
                                defaultValue={cand.notes || ''}
                                placeholder="Add a note about this candidate..."
                                onBlur={async (e) => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chro/candidates/${cand.id}`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                      body: JSON.stringify({ notes: e.target.value }),
                                    });
                                    toast.success('Note saved', { duration: 2000 });
                                  } catch { /* ignore */ }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: '100%', minHeight: 60, padding: '8px 10px', fontSize: 12,
                                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                                  borderRadius: 6, color: '#F2F6FA', resize: 'vertical', outline: 'none',
                                  fontFamily: 'Inter, sans-serif',
                                }}
                              />

                              {/* Candidate Room */}
                              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                                <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: '#AAB8C8', textTransform: 'uppercase', marginBottom: 8 }}>
                                  Candidate Room {cand.portal_activated ? '\u2713 Active' : ''}
                                </p>
                                {cand.portal_activated ? (
                                  <div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                      <span style={{ fontFamily: 'var(--fm)', fontSize: 12, color: 'var(--teal)' }}>PIN: {cand.portal_pin || '----'}</span>
                                      <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(cand.portal_pin || ''); toast.success('PIN copied'); }} style={{ fontSize: 10, color: '#AAB8C8', background: 'none', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer' }}>Copy PIN</button>
                                      <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`${window.location.origin}/candidate/${selectedSearch.id}/${cand.id}`); toast.success('Link copied'); }} style={{ fontSize: 10, color: '#AAB8C8', background: 'none', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer' }}>Copy Link</button>
                                      <button onClick={(e) => { e.stopPropagation(); window.open(`/candidate/${selectedSearch.id}/${cand.id}?preview=true`, '_blank'); }} style={{ fontSize: 10, color: 'var(--teal)', background: 'none', border: '1px solid rgba(0,212,200,0.3)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer' }}>Preview Room</button>
                                    </div>
                                    {cand.submission_submitted_at && <span style={{ fontSize: 10, color: '#2EC8A0' }}>Submission received</span>}
                                  </div>
                                ) : (
                                  <div>
                                    <p style={{ fontSize: 11, color: '#AAB8C8', marginBottom: 8 }}>Set up a personalised portal for this candidate</p>
                                    <button onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const token = localStorage.getItem('token');
                                        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chro/candidates/${cand.id}/activate-portal`, {
                                          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                          body: JSON.stringify({ message: `Hi ${cand.name}, we've identified your profile as a strong match for our ${selectedSearch.role_title} opportunity.` }),
                                        });
                                        if (resp.ok) {
                                          const data = await resp.json();
                                          toast.success(`Room activated. PIN: ${data.pin}`);
                                          const updated = await chroApi.getSearch(selectedSearch.id);
                                          setSelectedSearch(updated.data);
                                        }
                                      } catch { toast.error('Failed to activate room'); }
                                    }} style={{ fontSize: 11, fontWeight: 600, padding: '6px 14px', background: '#00D4C8', color: '#03050A', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                                      Activate Candidate Room
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedSearch.candidates.map((candidateId, idx) => (
                      <Card key={candidateId} className="glass-light">
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users className="h-4 w-4 text-primary" />
                            </div>
                            <span className="text-sm font-medium">Candidate {idx + 1}</span>
                          </div>
                          <Badge variant="outline">ID: {candidateId.slice(0, 8)}</Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>)}

              </div>

              {/* Timestamps */}
              <div className="mt-4 flex items-center gap-6 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Created: {new Date(selectedSearch.created_at).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Updated: {new Date(selectedSearch.updated_at).toLocaleDateString()}
                </span>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setSelectedSearch(null)}>
                  Close
                </Button>
                {getStageIndex(selectedSearch.status) < PIPELINE_STAGES.length - 1 && (() => {
                  const currentIdx = getStageIndex(selectedSearch.status);
                  const nextStage = PIPELINE_STAGES[currentIdx + 1];
                  const isGated = nextStage?.gate_before;
                  
                  if (isGated && selectedSearch.status === 'shortlist') {
                    return (
                      <Button
                        onClick={() => handleGateApproval('gate_1')}
                        disabled={advancing}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="gate-1-approve-btn"
                      >
                        {advancing ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ShieldCheck className="h-4 w-4 mr-2" />
                        )}
                        Review & Approve Shortlist
                      </Button>
                    );
                  }
                  
                  if (isGated && selectedSearch.status === 'engage') {
                    return (
                      <Button
                        onClick={() => handleGateApproval('gate_2')}
                        disabled={advancing}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="gate-2-approve-btn"
                      >
                        {advancing ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ShieldCheck className="h-4 w-4 mr-2" />
                        )}
                        Approve Stage Invitation
                      </Button>
                    );
                  }
                  
                  return (
                    <Button
                      onClick={handleAdvanceStage}
                      disabled={advancing}
                      data-testid="advance-stage-btn"
                    >
                      {advancing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRightCircle className="h-4 w-4 mr-2" />
                      )}
                      Advance to {nextStage?.label}
                    </Button>
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ChroLayout>
  );
};

const StageWithExplainer = (props) => (<><Stage {...props} /><DemoExplainer screen="THE_STAGE" /></>);
export default StageWithExplainer;
